import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  APIProvider,
  APILoadingStatus,
  useApiLoadingStatus,
} from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY, hasGoogleKey } from "./env.js";

// Libraries preloaded with the Google Maps script. Kept module-level so the
// array identity is stable across renders. `marker` is for AdvancedMarker,
// `places` for AutocompleteSuggestion/Place, `geocoding` for Geocoder.
const GOOGLE_LIBRARIES = ["marker", "places", "geocoding"];

// Detect Google-Maps auth/billing failure and fan out to React subscribers.
//
// In theory, `window.gm_authFailure` is Google's hook for this. In practice,
// some failure modes — `BillingNotEnabledMapError` in particular — only
// surface as a `console.error` log without ever invoking `gm_authFailure`.
// So we register BOTH paths synchronously at module load and forward to the
// same subscriber list.

let pendingAuthFailureHandlers = [];
let fallbackTriggered = false;

const triggerFallback = (reason) => {
  if (fallbackTriggered) return;
  fallbackTriggered = true;
  console.warn(
    "[maps] Falling back to MapLibre. Reason:",
    reason
  );
  for (const fn of pendingAuthFailureHandlers) {
    try {
      fn();
    } catch (err) {
      console.error("[maps] auth-failure handler threw", err);
    }
  }
};

if (typeof window !== "undefined") {
  // (1) Native Google hook — fires for actual API-key / auth errors.
  window.gm_authFailure = () => triggerFallback("gm_authFailure");

  // (2) console.error sniffer — catches BillingNotEnabledMapError,
  //     ApiNotActivatedMapError, RefererNotAllowedMapError, etc., which
  //     Google emits as plain console.error calls.
  const isGoogleMapsError = (text) =>
    text.includes("Google Maps JavaScript API error") ||
    text.includes("BillingNotEnabledMapError") ||
    text.includes("ApiNotActivatedMapError") ||
    text.includes("RefererNotAllowedMapError") ||
    text.includes("InvalidKeyMapError") ||
    text.includes("MissingKeyMapError");

  const originalError = console.error.bind(console);
  console.error = function patchedConsoleError(...args) {
    originalError(...args);
    try {
      const text = args
        .map((a) => (typeof a === "string" ? a : a && a.message ? a.message : ""))
        .join(" ");
      if (isGoogleMapsError(text)) {
        triggerFallback("Google Maps error in console: " + text.slice(0, 120));
      }
    } catch (_err) {
      // never let our sniffer break console.error
    }
  };
}

const subscribeAuthFailure = (fn) => {
  pendingAuthFailureHandlers.push(fn);
  // If the failure already happened before this subscriber registered (which
  // is the common case — the auth error fires synchronously when Google
  // tries to render the map, often before the React tree has finished
  // mounting), notify immediately.
  if (fallbackTriggered) {
    try {
      fn();
    } catch (err) {
      console.error("[maps] auth-failure handler threw", err);
    }
  }
  return () => {
    pendingAuthFailureHandlers = pendingAuthFailureHandlers.filter(
      (h) => h !== fn
    );
  };
};

const MapsContext = createContext({
  provider: "loading",
  googleLoaded: false,
  googleLoadError: null,
});

export const useMapProvider = () => useContext(MapsContext);

// Reads the APIProvider loading status and publishes the resolved provider.
// Has to live inside <APIProvider> for useApiLoadingStatus() to work.
const GoogleStatusBridge = ({ children }) => {
  const [forcedFallback, setForcedFallback] = useState(false);
  const status = useApiLoadingStatus();

  // Subscribe to the module-level gm_authFailure dispatcher. The dispatcher
  // itself was assigned to window at import time so Google has it the moment
  // it tries to read it; we just plug a React state-setter into it here.
  useEffect(() => {
    return subscribeAuthFailure(() => setForcedFallback(true));
  }, []);

  const loadFailed =
    status === APILoadingStatus.FAILED ||
    status === APILoadingStatus.AUTH_FAILURE;
  const isLoaded = status === APILoadingStatus.LOADED;

  const value = useMemo(() => {
    let provider;
    if (forcedFallback || loadFailed) provider = "maplibre";
    else if (isLoaded) provider = "google";
    else provider = "loading";
    return {
      provider,
      googleLoaded: isLoaded && !forcedFallback,
      googleLoadError: loadFailed ? status : null,
    };
  }, [forcedFallback, loadFailed, isLoaded, status]);

  return <MapsContext.Provider value={value}>{children}</MapsContext.Provider>;
};

// Sub-provider that actually attempts to load Google Maps.
const GoogleLoaderProvider = ({ children }) => (
  <APIProvider
    apiKey={GOOGLE_MAPS_API_KEY}
    libraries={GOOGLE_LIBRARIES}
    region='PK'
    onError={(err) => triggerFallback("APIProvider onError: " + String(err))}
  >
    <GoogleStatusBridge>{children}</GoogleStatusBridge>
  </APIProvider>
);

// When no Google key is configured, skip the loader entirely and go straight
// to MapLibre.
const MaplibreOnlyProvider = ({ children }) => {
  const value = useMemo(
    () => ({ provider: "maplibre", googleLoaded: false, googleLoadError: null }),
    []
  );
  return <MapsContext.Provider value={value}>{children}</MapsContext.Provider>;
};

export const MapsProvider = ({ children }) => {
  if (!hasGoogleKey) return <MaplibreOnlyProvider>{children}</MaplibreOnlyProvider>;
  return <GoogleLoaderProvider>{children}</GoogleLoaderProvider>;
};
